import { Alert, Box, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useReportingDashboardEmbedTokenQuery } from "../../../graphql/generated";
import MetabaseIframe from "./MetabaseIframe";

interface ReportPreviewProps {
  reportLabel: string;
  /** Saved filter values to apply on load. */
  params?: Record<string, string>;
  /** Dashboard tab to open. */
  tab?: string | null;
}

export default function ReportPreview({
  reportLabel,
  params,
  tab,
}: ReportPreviewProps) {
  const { t } = useTranslation("reporting");
  // Don't refetch on window focus. Focus bounces between the page and the
  // iframe as the user clicks around, and each refetch makes a new token that
  // reloads the embed and clears the filters they set. The token lasts long
  // enough for a working session, so we hold it steady while the page is open.
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { data, isLoading, error } = useReportingDashboardEmbedTokenQuery(
    { timezone },
    {
      refetchOnWindowFocus: false,
      staleTime: 30 * 60 * 1000,
    },
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const iframeUrl = data?.reportingDashboardEmbedToken?.iframeUrl;
  if (error || !iframeUrl) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {t("errors.loadReport")}
      </Alert>
    );
  }

  return (
    <MetabaseIframe
      src={iframeUrl}
      title={reportLabel}
      params={params}
      tab={tab}
    />
  );
}
